import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Separator } from './ui/separator';
import { Plus, Trash2, Sparkles, AlertCircle, Wand2, ChevronDown, Search, Check } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Inline flavour picker — replaces the native <select> which had two
 * problems on mobile: zero search affordance and a UI that visually
 * collapses on tap. This is a tap-to-open panel with a sticky search
 * input + tappable rows.
 */
function FlavourPicker({ value, onChange, options, disabledNames = [], testIdSuffix = '' }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(o => o.name.toLowerCase().includes(q) || (o.subcategory || '').toLowerCase().includes(q));
  }, [options, query]);
  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => { setOpen(v => !v); setQuery(''); }}
        className="w-full flex items-center justify-between bg-transparent border border-white/15 text-white h-10 px-3 rounded-sm font-sans text-sm hover:border-gold/40 transition-colors"
        data-testid={`mix-slot-trigger-${testIdSuffix}`}
      >
        <span className={value ? 'text-white' : 'text-zinc-500'}>{value || '— Choose flavour —'}</span>
        <ChevronDown className={`w-4 h-4 text-zinc-400 transition-transform ${open ? 'rotate-180' : ''}`} />
      </button>
      {open && (
        <>
          <div className="fixed inset-0 z-30" onClick={() => setOpen(false)} aria-hidden />
          <div className="absolute z-40 left-0 right-0 mt-1 bg-bg-secondary border border-white/15 rounded-sm shadow-lg max-h-64 overflow-hidden flex flex-col" data-testid={`mix-slot-list-${testIdSuffix}`}>
            <div className="flex items-center gap-2 px-2.5 py-2 border-b border-white/10 sticky top-0 bg-bg-secondary">
              <Search className="w-3.5 h-3.5 text-zinc-500 shrink-0" />
              <input
                autoFocus
                value={query}
                onChange={e => setQuery(e.target.value)}
                placeholder="Search flavours…"
                className="flex-1 bg-transparent outline-none text-white text-sm font-sans placeholder:text-zinc-600"
                data-testid={`mix-slot-search-${testIdSuffix}`}
              />
            </div>
            <div className="overflow-y-auto flex-1">
              {filtered.length === 0 && (
                <p className="text-zinc-500 text-sm font-sans px-3 py-3 text-center">No matches</p>
              )}
              {filtered.map(opt => {
                const disabled = disabledNames.includes(opt.name) && opt.name !== value;
                const selected = opt.name === value;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    disabled={disabled}
                    onClick={() => { onChange(opt.name); setOpen(false); }}
                    className={`w-full flex items-center justify-between px-3 py-2 text-left font-sans text-sm transition-colors ${disabled ? 'opacity-40 cursor-not-allowed' : 'hover:bg-white/5'} ${selected ? 'text-gold' : 'text-white'}`}
                    data-testid={`mix-slot-opt-${opt.id}`}
                  >
                    <span className="truncate">
                      {opt.name}
                      {opt.subcategory && <span className="text-zinc-500 text-xs ml-1.5">· {opt.subcategory}</span>}
                    </span>
                    {selected && <Check className="w-3.5 h-3.5 text-gold shrink-0" />}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Custom Shisha Mix builder.
 *
 * Surface: tapped from the "Create Your Own Mix" menu card on the Shisha
 * subcategory. Lets the customer pick 2 or 3 flavours and tune the ratio
 * between them with sliders. The total always stays at 100% via a tiny
 * auto-rebalancing helper — adjusting one slot redistributes the deficit
 * equally across the other slots so the user never has to do math.
 *
 * Props:
 *   open: bool
 *   onOpenChange: fn
 *   item: the "Create Your Own Mix" menu item (has head_options + add_ons)
 *   availableFlavours: [{ id, name, subcategory }]
 *   onAdd: ({ mix_flavours, head_label, head_price, addOns, displayName, unitPrice }) => void
 */
export default function CustomMixDialog({ open, onOpenChange, item, availableFlavours = [], onAdd }) {
  // Each slot is { name, percent }. Customers can add up to 3 slots.
  const [slots, setSlots] = useState([]);
  const [selectedHead, setSelectedHead] = useState(null);
  const [selectedAddOns, setSelectedAddOns] = useState([]);

  // All mix ratios must be multiples of 10 so the picker reads like a clean
  // recipe ("60/40", "50/30/20") rather than fiddly percentages. Sliders step
  // by 10 and we round every rebalanced share to the nearest 10 below.
  const STEP = 10;
  const MIN_PER_SLOT = 10;
  const roundTo10 = (n) => Math.round(n / STEP) * STEP;

  // Stable React-key generator — index-as-key broke input focus + state
  // binding when removing a middle slot (removeSlot pops idx=1 → React
  // rebinds slot 2's UI state to the now-shifted slot 1).
  const nextSlotKey = useRef(0);
  const newSlot = (name, percent) => ({ _key: ++nextSlotKey.current, name, percent });

  // Keep `availableFlavours` reachable inside event handlers without making
  // it a useEffect dependency. The parent recomputes the array on every
  // poll tick (orders refresh every 5s); if we depend on the array
  // reference, the reset effect re-fires and wipes the customer's picked
  // 3rd flavour mid-flow. This ref-based read keeps state stable.
  const flavoursRef = useRef(availableFlavours);
  useEffect(() => { flavoursRef.current = availableFlavours; }, [availableFlavours]);

  // Reset ONLY when the dialog opens (open transition false→true) or the
  // underlying menu item changes — never on parent re-renders.
  useEffect(() => {
    if (!open) return;
    const flavs = flavoursRef.current || [];
    setSlots([
      newSlot(flavs[0]?.name || '', 50),
      newSlot(flavs[1]?.name || '', 50),
    ]);
    setSelectedHead(item?.head_options?.[0] || null);
    setSelectedAddOns([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, item?.id]);

  const total = useMemo(() => slots.reduce((s, x) => s + (x.percent || 0), 0), [slots]);
  const headPrice = Number(selectedHead?.price || 0);
  const addOnPrice = selectedAddOns.reduce((s, a) => s + Number(a.price || 0), 0);
  const unitPrice = Number(item?.price || 0) + headPrice + addOnPrice;

  // Distribute a delta across the OTHER slots so the total stays = 100, while
  // keeping every share on a 10s grid. Each non-last share is rounded; the
  // last absorbs any drift (also always a 10 because the inputs are 10s).
  const rebalance = (changedIndex, newPercent) => {
    const maxForChanged = 100 - MIN_PER_SLOT * (slots.length - 1);
    const clamped = Math.max(MIN_PER_SLOT, Math.min(maxForChanged, roundTo10(newPercent)));
    const others = slots.map((s, i) => ({ ...s, _i: i })).filter(s => s._i !== changedIndex);
    const remaining = 100 - clamped;
    let next = slots.map((s, i) => i === changedIndex ? { ...s, percent: clamped } : s);
    if (others.length === 0) return next;
    const otherSum = others.reduce((s, x) => s + x.percent, 0) || 1;
    let assigned = 0;
    others.forEach((o, idx) => {
      const isLast = idx === others.length - 1;
      const share = isLast
        ? remaining - assigned
        : Math.max(MIN_PER_SLOT, roundTo10((o.percent / otherSum) * remaining));
      assigned += share;
      next[o._i] = { ...o, percent: share };
    });
    // Defensive: if rounding dropped the last slot below the 10 minimum,
    // borrow from earlier other-slots to restore it.
    const lastIdx = others[others.length - 1]._i;
    if (next[lastIdx].percent < MIN_PER_SLOT) {
      let deficit = MIN_PER_SLOT - next[lastIdx].percent;
      next[lastIdx] = { ...next[lastIdx], percent: MIN_PER_SLOT };
      for (let i = others.length - 2; i >= 0 && deficit > 0; i--) {
        const idx = others[i]._i;
        const canGive = next[idx].percent - MIN_PER_SLOT;
        const give = Math.min(canGive, deficit);
        next[idx] = { ...next[idx], percent: next[idx].percent - give };
        deficit -= give;
      }
    }
    return next;
  };

  const updateSlotName = (i, name) => setSlots(prev => prev.map((s, idx) => idx === i ? { ...s, name } : s));
  const updateSlotPercent = (i, newPercent) => {
    setSlots(prev => rebalance(i, Number(newPercent) || 0));
  };
  const addSlot = () => {
    if (slots.length >= 3) return;
    // New slot defaults to 20%. The remaining 80% is split among the existing
    // slots proportionally and rounded to 10s; the last existing slot
    // absorbs any drift so the total stays 100.
    const NEW_SLOT_SHARE = 20;
    const remaining = 100 - NEW_SLOT_SHARE;
    const sum = slots.reduce((s, x) => s + x.percent, 0) || 1;
    let assigned = 0;
    const next = slots.map((s, idx) => {
      const isLast = idx === slots.length - 1;
      const share = isLast
        ? remaining - assigned
        : Math.max(MIN_PER_SLOT, roundTo10((s.percent / sum) * remaining));
      assigned += share;
      return { ...s, percent: share };
    });
    const flavs = flavoursRef.current || [];
    // Leave the new slot's name BLANK so the picker prompts the customer to
    // choose. (Auto-filling it was confusing; users thought a random
    // flavour had been selected for them.)
    setSlots([...next, newSlot('', NEW_SLOT_SHARE)]);
    // Reference flavs so the linter sees the dependency; we just don't
    // auto-pick anymore.
    void flavs;
  };
  const removeSlot = (i) => {
    if (slots.length <= 2) {
      toast.error('A mix needs at least 2 flavours');
      return;
    }
    const removed = slots[i].percent;
    const kept = slots.filter((_, idx) => idx !== i);
    // Split removed share among the remaining slots in 10s; last absorbs drift.
    const each = roundTo10(removed / kept.length);
    let assigned = 0;
    setSlots(kept.map((s, idx) => {
      const isLast = idx === kept.length - 1;
      const bonus = isLast ? removed - assigned : each;
      assigned += bonus;
      return { ...s, percent: s.percent + bonus };
    }));
  };

  const submit = () => {
    if (slots.length < 2 || slots.length > 3) {
      toast.error('Pick 2 or 3 flavours');
      return;
    }
    const names = slots.map(s => s.name.trim()).filter(Boolean);
    if (names.length !== slots.length) {
      toast.error('Choose a flavour for every slot');
      return;
    }
    if (new Set(names).size !== names.length) {
      toast.error('Each flavour must be unique');
      return;
    }
    if (total !== 100) {
      toast.error(`Ratios must total 100% (currently ${total}%)`);
      return;
    }
    const mixStr = slots.map(s => `${s.name} ${s.percent}%`).join(' + ');
    const headLabel = selectedHead?.label || null;
    const baseName = `Custom Mix · ${mixStr}`;
    const displayName = headLabel ? `${baseName} + ${headLabel}` : baseName;
    onAdd && onAdd({
      mix_flavours: slots.map(s => ({ name: s.name, percent: s.percent })),
      head_label: headLabel,
      head_price: headPrice,
      addOns: [...selectedAddOns],
      displayName,
      unitPrice,
    });
    onOpenChange(false);
  };

  const pickedNames = slots.map(s => s.name).filter(Boolean);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/* `max-h` uses `dvh` (dynamic viewport height) so iOS Safari's
          collapsing/expanding bottom toolbar doesn't clip the "Choose Your
          Head" + "Add to Cart" buttons below the chrome. Combined with the
          dialog base class's `top-4 translate-y-0` on mobile, the form
          stays anchored at the top with scrollable content beneath. */}
      <DialogContent aria-describedby={undefined} className="bg-bg-secondary border border-white/10 max-w-md max-h-[calc(100dvh-2rem)] overflow-y-auto" data-testid="custom-mix-dialog">
        <DialogHeader>
          <DialogTitle className="font-serif text-white flex items-center gap-2">
            <Wand2 className="w-4 h-4 text-gold" />
            Create Your Own Mix
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 pt-2">
          <p className="text-xs text-zinc-400 font-sans leading-relaxed">
            Pick 2 or 3 flavours and dial in the ratio. Flat <span className="text-gold">${Number(item?.price || 0).toFixed(2)}</span> — no upcharge for premium flavours.
          </p>

          {/* Flavour slots */}
          <div className="space-y-2">
            {slots.map((slot, i) => (
              <div key={slot._key ?? i} className="rounded-sm border border-white/10 bg-white/[0.02] p-3 space-y-2" data-testid={`mix-slot-${i}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-[10px] uppercase tracking-[0.15em] text-zinc-500 font-sans">Flavour {i + 1}</span>
                  {slots.length > 2 && (
                    <button
                      onClick={() => removeSlot(i)}
                      className="w-6 h-6 rounded-sm hover:bg-red-500/15 text-red-400 flex items-center justify-center"
                      data-testid={`mix-slot-remove-${i}`}
                      title="Remove this flavour"
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                <FlavourPicker
                  value={slot.name}
                  onChange={(name) => updateSlotName(i, name)}
                  options={availableFlavours}
                  disabledNames={pickedNames.filter(n => n !== slot.name)}
                  testIdSuffix={String(i)}
                />
                <div className="space-y-1">
                  <div className="flex items-center justify-between">
                    <span className="text-zinc-400 font-sans text-xs">Share</span>
                    <span className="text-gold font-mono text-sm font-semibold" data-testid={`mix-slot-pct-${i}`}>{slot.percent}%</span>
                  </div>
                  {/* Stepper — tappable +/- buttons that move in 10% steps.
                      Replaces the drag-slider which was finicky on touch and
                      caused customers to accidentally over- or under-set the
                      ratio. Bounds are dynamic: MIN_PER_SLOT to
                      100 − MIN_PER_SLOT × (other slots). The rebalance helper
                      keeps the total at exactly 100 by redistributing the
                      delta across the other slots. */}
                  {(() => {
                    const max = 100 - MIN_PER_SLOT * (slots.length - 1);
                    const canDec = slot.percent > MIN_PER_SLOT;
                    const canInc = slot.percent < max;
                    return (
                      <div className="grid grid-cols-3 gap-2 items-stretch">
                        <button
                          type="button"
                          onClick={() => updateSlotPercent(i, slot.percent - STEP)}
                          disabled={!canDec}
                          className="h-10 rounded-sm border border-white/15 text-gold hover:border-gold/50 hover:bg-gold/10 flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed transition-colors active:scale-95"
                          aria-label="Decrease share by 10%"
                          data-testid={`mix-slot-dec-${i}`}
                        >
                          <span className="text-lg font-semibold leading-none">−</span>
                        </button>
                        <div className="h-10 rounded-sm border border-gold/30 bg-gold/10 flex items-center justify-center">
                          <span className="text-gold font-mono text-base font-bold" aria-live="polite">{slot.percent}%</span>
                        </div>
                        <button
                          type="button"
                          onClick={() => updateSlotPercent(i, slot.percent + STEP)}
                          disabled={!canInc}
                          className="h-10 rounded-sm border border-white/15 text-gold hover:border-gold/50 hover:bg-gold/10 flex items-center justify-center disabled:opacity-30 disabled:cursor-not-allowed transition-colors active:scale-95"
                          aria-label="Increase share by 10%"
                          data-testid={`mix-slot-inc-${i}`}
                        >
                          <span className="text-lg font-semibold leading-none">+</span>
                        </button>
                      </div>
                    );
                  })()}
                </div>
              </div>
            ))}
            {slots.length < 3 && (
              <Button
                onClick={addSlot}
                variant="ghost"
                className="w-full h-10 rounded-sm border border-dashed border-gold/30 text-gold hover:bg-gold/10 hover:border-gold/50 font-sans text-xs"
                data-testid="mix-add-slot"
              >
                <Plus className="w-3.5 h-3.5 mr-1" /> Add a third flavour
              </Button>
            )}
          </div>

          {/* Total indicator */}
          <div className={`rounded-sm px-3 py-2 flex items-center justify-between font-sans text-xs ${total === 100 ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-300' : 'bg-amber-500/10 border border-amber-500/30 text-amber-300'}`} data-testid="mix-total-indicator">
            <span className="flex items-center gap-1.5">
              {total === 100 ? <Sparkles className="w-3.5 h-3.5" /> : <AlertCircle className="w-3.5 h-3.5" />}
              Ratio total
            </span>
            <span className="font-mono font-bold">{total}%</span>
          </div>

          <Separator className="bg-white/5" />

          {/* Head options */}
          {item?.head_options?.length > 0 && (
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Choose your head</Label>
              <div className="grid grid-cols-2 gap-2">
                {item.head_options.map((h) => {
                  const isActive = selectedHead && selectedHead.label === h.label;
                  return (
                    <button
                      key={h.label}
                      type="button"
                      onClick={() => setSelectedHead(h)}
                      className={`flex flex-col items-start text-left px-3 py-2 rounded-sm border transition-all text-xs font-sans ${isActive ? 'border-gold bg-gold/10 text-gold' : 'border-white/10 text-zinc-400 hover:border-white/30'}`}
                      data-testid={`mix-head-${h.label.replace(/\s+/g, '-').toLowerCase()}`}
                    >
                      <span className="font-medium">{h.label}</span>
                      <span className="text-[11px] opacity-80 mt-0.5">{Number(h.price) > 0 ? `+ $${Number(h.price).toFixed(2)}` : 'Included'}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Add-ons */}
          {item?.add_ons?.length > 0 && (
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-zinc-400 font-sans">Add-ons</Label>
              {item.add_ons.map((addon) => {
                const selected = selectedAddOns.find(a => a.name === addon.name);
                return (
                  <button
                    key={addon.name}
                    onClick={() => setSelectedAddOns(prev => selected ? prev.filter(a => a.name !== addon.name) : [...prev, addon])}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-sm border transition-all text-sm font-sans ${selected ? 'border-gold bg-gold/10 text-gold' : 'border-white/10 text-zinc-400'}`}
                    data-testid={`mix-addon-${addon.name.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    <span>{addon.name}</span>
                    <span>+${Number(addon.price).toFixed(2)}</span>
                  </button>
                );
              })}
            </div>
          )}

          <Separator className="bg-white/5" />

          <div className="flex justify-between text-white font-sans font-semibold">
            <span>Total</span>
            <span className="text-gold font-mono">${unitPrice.toFixed(2)}</span>
          </div>

          <Button
            onClick={submit}
            disabled={total !== 100 || slots.some(s => !s.name)}
            className="w-full h-11 bg-gold text-black font-bold tracking-[0.1em] uppercase text-sm rounded-sm hover:bg-gold-light font-sans disabled:opacity-50 disabled:hover:bg-gold"
            data-testid="mix-confirm"
          >
            Add Mix to Cart
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Unused import shim — `Input` was used in earlier revisions; keeping the
// import won't break lint but explicitly silencing it for clarity.
void Input;
